/**
 * useFeatureFlags - Hook for checking feature visibility
 * 
 * Fetches enabled features on mount and provides helper functions
 * to check if specific features are enabled for the current user.
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Context for feature flags
const FeatureFlagsContext = createContext({
  flags: {},
  isBetaTester: false,
  isFreeProduct: false,
  loading: true,
  isEnabled: () => true, // Default to true so nothing breaks while loading
  refresh: () => {},
});

/**
 * Feature Flags Provider
 * Wrap your app with this to provide feature flag context
 */
export const FeatureFlagsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [flags, setFlags] = useState({});
  const [isBetaTester, setIsBetaTester] = useState(false);
  const [isFreeProduct, setIsFreeProduct] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadFlags = useCallback(async () => {
    // Only load flags if user is authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/api/feature-flags/enabled');
      setFlags(response.data.enabled || {});
      setIsBetaTester(response.data.is_beta_tester || false);
      setIsFreeProduct(response.data.is_free_product || false);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
      // On error, default everything to enabled so we don't break the app
      setFlags({});
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadFlags();
  }, [loadFlags, user]);

  /**
   * Check if a feature is enabled
   * @param {string} featureId - The feature ID to check
   * @returns {boolean} - True if enabled, false if disabled
   */
  const isEnabled = useCallback((featureId) => {
    // While loading or if flag not found, default to true (don't break anything)
    if (loading) return true;
    if (!(featureId in flags)) return true;
    return flags[featureId];
  }, [flags, loading]);

  /**
   * Check if any feature in a list is enabled
   * Useful for checking if a group header should be shown
   */
  const isAnyEnabled = useCallback((featureIds) => {
    return featureIds.some(id => isEnabled(id));
  }, [isEnabled]);

  /**
   * Check if all features in a list are enabled
   */
  const isAllEnabled = useCallback((featureIds) => {
    return featureIds.every(id => isEnabled(id));
  }, [isEnabled]);

  return (
    <FeatureFlagsContext.Provider value={{
      flags,
      isBetaTester,
      isFreeProduct,
      loading,
      isEnabled,
      isAnyEnabled,
      isAllEnabled,
      refresh: loadFlags,
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Hook to access feature flags
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

// Convenience constants for feature IDs
export const FEATURE_IDS = {
  // Product Tabs
  TAB_SUMMARY: 'tab_summary',
  TAB_PERSONAS: 'tab_personas',
  TAB_GOALS: 'tab_goals',
  TAB_STRATEGY: 'tab_strategy',
  TAB_MARKET: 'tab_market',
  TAB_INITIATIVES: 'tab_initiatives',
  TAB_EPICS: 'tab_epics',
  TAB_FEATURES: 'tab_features',
  TAB_IDEATION: 'tab_ideation',
  TAB_RELEASES: 'tab_releases',
  TAB_KANBAN: 'tab_kanban',
  TAB_ARCHITECT: 'tab_architect',
  TAB_DELIVERY: 'tab_delivery',
  
  // Ideation Tools
  IDEATION_RESEARCH: 'ideation_research',      // Controls Deep Research section
  IDEATION_INTERVIEWS: 'ideation_interviews',  // Controls Feedback Forms
  IDEATION_STACKRANKING: 'ideation_stackranking',
  
  // Co-pilots
  COPILOT_FEATURE: 'copilot_feature',
  COPILOT_EPIC: 'copilot_epic',
  COPILOT_GOAL: 'copilot_goal',
  COPILOT_INITIATIVE: 'copilot_initiative',
  COPILOT_PERSONA: 'copilot_persona',
  COPILOT_FEATURES_COLLECTION: 'copilot_features_collection',
  COPILOT_EPICS_COLLECTION: 'copilot_epics_collection',
  COPILOT_GOALS_COLLECTION: 'copilot_goals_collection',
  COPILOT_INITIATIVES_COLLECTION: 'copilot_initiatives_collection',
  COPILOT_PERSONAS_COLLECTION: 'copilot_personas_collection',
  COPILOT_REQUESTS: 'copilot_requests',
  COPILOT_RELEASES: 'copilot_releases',
  COPILOT_STRATEGY: 'copilot_strategy',
  COPILOT_MARKET: 'copilot_market',
  COPILOT_KANBAN: 'copilot_kanban',
  COPILOT_VISUALIZATION: 'copilot_visualization',
  COPILOT_COLLABORATION: 'copilot_collaboration',
  COPILOT_EXECUTIVE: 'copilot_executive',
  COPILOT_PRODUCT_WIZARD: 'copilot_product_wizard',
  
  // Org Navigation
  ORG_VISUALIZATIONS: 'org_visualizations',
  ORG_COLLABORATION: 'org_collaboration',
  ORG_EXECUTIVE_AI: 'org_executive_ai',
  ORG_DASHBOARD: 'org_dashboard',
  ORG_TEAM: 'org_team',
  
  // Integrations
  INTEGRATION_MCP: 'integration_mcp',
  INTEGRATION_GITHUB: 'integration_github',
  INTEGRATION_JIRA: 'integration_jira',
  INTEGRATION_LINEAR: 'integration_linear',
  INTEGRATION_ADO: 'integration_ado',
  
  // Help & Documentation
  HELP_FULL_DOCS: 'help_full_docs',
  
  // Marketing/Public
  DEMO_ENABLED: 'demo_enabled',
};

// Map context types to copilot feature flags
export const COPILOT_CONTEXT_FLAGS = {
  'feature': FEATURE_IDS.COPILOT_FEATURE,
  'epic': FEATURE_IDS.COPILOT_EPIC,
  'goal': FEATURE_IDS.COPILOT_GOAL,
  'initiative': FEATURE_IDS.COPILOT_INITIATIVE,
  'persona': FEATURE_IDS.COPILOT_PERSONA,
  'features': FEATURE_IDS.COPILOT_FEATURES_COLLECTION,
  'epics': FEATURE_IDS.COPILOT_EPICS_COLLECTION,
  'goals': FEATURE_IDS.COPILOT_GOALS_COLLECTION,
  'initiatives': FEATURE_IDS.COPILOT_INITIATIVES_COLLECTION,
  'personas': FEATURE_IDS.COPILOT_PERSONAS_COLLECTION,
  'requests': FEATURE_IDS.COPILOT_REQUESTS,
  'releases': FEATURE_IDS.COPILOT_RELEASES,
  'strategy': FEATURE_IDS.COPILOT_STRATEGY,
  'market': FEATURE_IDS.COPILOT_MARKET,
  'kanban': FEATURE_IDS.COPILOT_KANBAN,
  'visualization': FEATURE_IDS.COPILOT_VISUALIZATION,
  'collaboration': FEATURE_IDS.COPILOT_COLLABORATION,
  'executive': FEATURE_IDS.COPILOT_EXECUTIVE,
  'wizard': FEATURE_IDS.COPILOT_PRODUCT_WIZARD,
};

export default useFeatureFlags;
